import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary';
import NotificationBanner from './NotificationBanner';
import ChildSwitcher from './parent/ChildSwitcher';
import ParentTabBar from './parent/ParentTabBar';
import OtherChildrenNotice from './parent/OtherChildrenNotice';
import { useAppDispatch } from '../hooks/redux';
import { useAuth } from '../hooks/useAuth';
import { useActiveStudent } from '../hooks/useActiveStudent';
import { useLocationContext } from '../hooks/useLocationContext';
import { useScopedPath } from './ScopedLink';
import { activeNavIndex } from './nav-active';
import { Membership } from '../types';
import { brand } from '../branding';
import { logout, switchMembership, setUser } from '../store/slices/authSlice';
import { remapActiveIndex } from '../store/membershipKey';
import { usePushNotifications } from '../hooks/usePushNotifications';
import {
  useGetInvitationCountQuery,
  useGetMyUnreadCountQuery,
  useGetMyObservationsUnreadCountQuery,
  useGetContextsQuery,
  useSwitchContextMutation,
  localApi,
} from '../store/api/localApi';
import {
  HomeIcon,
  AcademicCapIcon,
  BuildingOfficeIcon,
  BuildingLibraryIcon,
  Bars3Icon,
  XMarkIcon,
  ClipboardDocumentCheckIcon,
  CalendarIcon,
  ChatBubbleLeftIcon,
  SparklesIcon,
  DocumentTextIcon,
  RectangleGroupIcon,
  UserGroupIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  CheckIcon,
  FireIcon,
  ExclamationTriangleIcon,
  BeakerIcon,
  ClipboardDocumentListIcon,
  ArchiveBoxIcon,
  IdentificationIcon,
  BellIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';

type NavItem = {
  name: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** Optional section heading rendered ABOVE this item. Used to group
   *  items under a small uppercase label (e.g. "Compliance"). Items with
   *  the same sectionHeader value back-to-back share one header. */
  sectionHeader?: string;
  /** Stable testid override for E2E (defaults to `nav-<kebab-name>`). */
  testId?: string;
  showBadge?: boolean;
  showMessagesBadge?: boolean;
  showObservationsBadge?: boolean;
  /** Parent items the phone tab bar already carries; the mobile drawer skips them. */
  inTabBar?: boolean;
  /** Active only on its own path, not on sub-paths (see activeNavIndex). */
  exact?: boolean;
};

const ROLE_COLORS: Record<string, string> = {
  PARENT: 'bg-green-100 text-green-800',
  STAFF: 'bg-blue-100 text-blue-800',
  LOCATION_ADMIN: 'bg-purple-100 text-purple-800',
  ORG_ADMIN: 'bg-orange-100 text-orange-800',
  SUPER_ADMIN: 'bg-red-100 text-red-800',
};

const ROLE_LABELS: Record<string, string> = {
  PARENT: 'Parent',
  STAFF: 'Staff',
  LOCATION_ADMIN: 'Location Admin',
  ORG_ADMIN: 'Org Admin',
  SUPER_ADMIN: 'Super Admin',
};

// LocalStorage key prefix for collapsed-section state. Per-section so users
// can independently collapse "Compliance", "Admin" etc. as more groups land.
const COLLAPSED_SECTION_STORAGE_PREFIX = 'sproutly.nav.sectionCollapsed.';

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Which named sidebar sections the user has collapsed. Persisted per-
  // section in localStorage so the choice survives reloads and other tabs.
  // Mobile ignores this — the mobile sidebar is a transient flyout where
  // hiding items behind an extra tap is worse than the vertical space win.
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(() => {
    if (typeof window === 'undefined') return {};
    const out: Record<string, boolean> = {};
    try {
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (!key?.startsWith(COLLAPSED_SECTION_STORAGE_PREFIX)) continue;
        const section = key.slice(COLLAPSED_SECTION_STORAGE_PREFIX.length);
        out[section] = window.localStorage.getItem(key) === '1';
      }
    } catch {
      /* private mode / storage disabled — sections just stay expanded */
    }
    return out;
  });

  const toggleSection = useCallback((section: string) => {
    setCollapsedSections((prev) => {
      // Sections are collapsed BY DEFAULT (see the render logic: absent-or-true
      // = collapsed, explicit false = expanded). Toggle from the EFFECTIVE
      // state, not the raw stored value — otherwise a first click on a
      // never-touched (undefined) section computes `!undefined === true` and
      // re-collapses the already-collapsed section instead of expanding it.
      const currentlyCollapsed = prev[section] !== false;
      const nextCollapsed = !currentlyCollapsed;
      const next = { ...prev, [section]: nextCollapsed };
      try {
        window.localStorage.setItem(
          `${COLLAPSED_SECTION_STORAGE_PREFIX}${section}`,
          nextCollapsed ? '1' : '0'
        );
      } catch {
        /* ignore — state still tracked in memory for the session */
      }
      return next;
    });
  }, []);
  const locationPickerRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const routerLocation = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    user,
    activeRole,
    isParent,
    isOrgLevel,
    isSuperAdmin,
    activeOrganizationName,
    activeMembershipIndex,
  } = useAuth();
  const [switchContext] = useSwitchContextMutation();
  const {
    isInLocation,
    activeLocationName,
    activeLocationRole,
    exitLocation,
    isInOrg,
    activeOrgName,
    exitOrg,
  } = useLocationContext();
  const scopedPath = useScopedPath();

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (locationPickerRef.current && !locationPickerRef.current.contains(event.target as Node)) {
        setLocationPickerOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // All switchable memberships for the role picker. We surface the
  // SUPER_ADMIN membership too for hybrid users (e.g. a super admin who is
  // also a parent) so they can flip back into the platform view from
  // whatever location/parent context they're in.
  //
  // The backend includes a SUPER_ADMIN membership in the login response for
  // platform admins, so we don't synthesize one — we just stop filtering it
  // out. If the backend ever drops that convention we'd need to synthesize.
  //
  // The login-time memberships go stale when a user gains a new role mid
  // session (e.g. a SUPER_ADMIN whose email later becomes the primary parent
  // on a newly-created student). We resync against /session/contexts on
  // mount so the dropdown reflects everything the server currently lets the
  // user switch into. Without this, a re-login is the only way to surface
  // the new context.
  // Skip the resync for a user with no memberships: /session/contexts 403s
  // ("no active memberships") for a dormant parent who hasn't finished
  // invite-signup, and there's nothing to resync anyway. The NoActiveMembership
  // guard already handles that state; firing this would just log a noisy error.
  const { data: contextsData } = useGetContextsQuery(undefined, {
    skip: !user || (user.memberships?.length ?? 0) === 0,
  });
  useEffect(() => {
    if (!user || !contextsData?.availableContexts) return;
    const remote = contextsData.availableContexts;
    const keyOf = (m: { role?: string; locationId?: string; organizationId?: string }) =>
      `${m.role || ''}|${m.locationId || ''}|${m.organizationId || ''}`;
    const localKeys = new Set((user.memberships || []).map(keyOf));
    const remoteKeys = new Set(remote.map(keyOf));
    const sameSet =
      localKeys.size === remoteKeys.size && [...localKeys].every((k) => remoteKeys.has(k));
    if (sameSet) return;
    // Build the new memberships from contexts, carrying over permissions /
    // children / createdAt from any pre-existing entry that matches by key
    // so existing UI gates keep their fine-grained data.
    const oldMemberships = user.memberships || [];
    const existing = new Map(oldMemberships.map((m) => [keyOf(m), m]));
    const merged = remote.map((c) => {
      const prior = existing.get(keyOf(c));
      return {
        userId: user.id,
        role: c.role as Membership['role'],
        locationId: c.locationId,
        locationName: c.locationName,
        organizationId: c.organizationId,
        organizationName: c.organizationName,
        permissions: prior?.permissions || [],
        createdAt: prior?.createdAt || new Date().toISOString(),
      };
    });
    // The active context is an index into `memberships`. `merged` may be in a
    // different order than the login-time array, so remap the index to keep it
    // pointing at the SAME membership — otherwise the user's active role/
    // location (and the X-Active-Context header) silently changes mid-session.
    const nextIndex = remapActiveIndex(oldMemberships, merged, activeMembershipIndex);
    dispatch(setUser({ ...user, memberships: merged }));
    if (nextIndex !== activeMembershipIndex) {
      dispatch(switchMembership(nextIndex));
    }
  }, [contextsData, user, dispatch, activeMembershipIndex]);

  const switchableMemberships = useMemo(() => {
    if (!user) return [];
    return user.memberships;
  }, [user]);

  // ========== Navigation items based on scope ==========

  // SUPER_ADMIN top level (not inside any org)
  const superAdminTopNav: NavItem[] = useMemo(
    () => [
      { name: 'Dashboard', href: scopedPath('/dashboard'), icon: HomeIcon },
      { name: 'Organizations', href: '/organizations', icon: BuildingLibraryIcon },
      { name: 'Team', href: '/team', icon: UserGroupIcon, testId: 'nav-team' },
      {
        name: 'Messages',
        href: scopedPath('/messages'),
        icon: ChatBubbleLeftIcon,
        showMessagesBadge: true,
      },
    ],
    [scopedPath]
  );

  // Org-level nav (ORG_ADMIN always, or SUPER_ADMIN inside an org but not a location)
  const orgLevelNav: NavItem[] = useMemo(
    () => [
      { name: 'Dashboard', href: scopedPath('/dashboard'), icon: HomeIcon },
      { name: 'Locations', href: scopedPath('/locations'), icon: BuildingOfficeIcon },
      { name: 'Forms', href: scopedPath('/forms'), icon: DocumentTextIcon },
      {
        name: 'Messages',
        href: scopedPath('/messages'),
        icon: ChatBubbleLeftIcon,
        showMessagesBadge: true,
      },
      { name: 'Team', href: scopedPath('/team'), icon: UserGroupIcon },
      // Per-org custom incident fields admin (Task 24). This nav is only
      // ever shown to ORG_ADMIN/SUPER_ADMIN (isOrgLevel && !isInLocation —
      // see the `navigation` selection below), matching the backend's
      // org:manage gate exactly — no extra role check needed here.
      {
        name: 'Incident Fields',
        href: scopedPath('/incident-fields'),
        icon: ExclamationTriangleIcon,
        testId: 'nav-incident-fields',
      },
    ],
    [scopedPath]
  );

  // Parent shell is gated solely on the active membership (Redux). The
  // ParentRoute guard handles mid-switch transitions inside /parent/*; the
  // Layout doesn't need to second-guess that. activeLocationRole is kept as a
  // fallback ONLY for pure single-membership parents (guards against an
  // early-auth race where activeRole hasn't landed yet). For hybrid users
  // with a membership picker it would pin the shell to "parent" forever
  // because activeLocationRole is hard-coded to the first location membership
  // and never updates on switch (Bug 5 — the kid picker + parent sidebar items
  // stayed visible after toggling to Staff).
  const effectiveIsParent =
    isParent || (switchableMemberships.length <= 1 && activeLocationRole === 'PARENT');

  // Parent active-student context — drives the child-first nav. The switcher
  // lives in the top bar; child pages link to the active child, or to the
  // remembered child on family/account pages (homeChildId).
  const {
    activeStudent,
    homeChildId,
    isFamilyPage,
    isChooserPage,
    children: parentChildren,
  } = useActiveStudent();
  // The phone tab bar appears once a child is decided — but never on the
  // chooser, even when a remembered child sets homeChildId there.
  const parentTabBarVisible = Boolean(homeChildId) && !isChooserPage;

  // Hold the last resolved home label so the sidebar doesn't visibly drop
  // the kid's name during role-switch transitions. Without this the user
  // sees "Dashboard" → "Home" → "Olivia" in quick succession; with it the
  // generic "Home" never flashes once we've shown a real label.
  const lastParentHomeLabelRef = useRef<string | null>(null);
  // Named after the home child, not the URL's child, so family/account/settings
  // pages (which name no child) still read e.g. "Aoife" rather than "Home".
  const computedParentHomeLabel =
    parentChildren.find((c) => c.id === homeChildId)?.firstName ?? activeStudent?.firstName ?? null;
  if (computedParentHomeLabel) {
    lastParentHomeLabelRef.current = computedParentHomeLabel;
  }

  const locationLevelNav: NavItem[] = useMemo(() => {
    if (effectiveIsParent) {
      // Prefer the resolved label, then the cached previous label (avoids a
      // brief "Home" flash during role-switch redirects), then the generic
      // fallback as a last resort.
      const homeLabel = computedParentHomeLabel ?? lastParentHomeLabelRef.current ?? 'Home';
      const base = homeChildId ? `/parent/${homeChildId}` : '/parent';
      // No sectionHeader: sections are collapsed by default and would hide
      // Forms on desktop. Inbox and Policies are family pages (every child).
      return [
        { name: homeLabel, href: base, exact: true, icon: HomeIcon, testId: 'nav-home', inTabBar: true },
        { name: 'Schedule', href: `${base}/calendar`, icon: CalendarIcon, testId: 'nav-schedule', inTabBar: true },
        { name: 'Inbox', href: '/parent/family/inbox', icon: ChatBubbleLeftIcon, testId: 'nav-inbox', showMessagesBadge: true, inTabBar: true },
        { name: 'Updates', href: `${base}/updates`, icon: SparklesIcon, testId: 'nav-updates', showObservationsBadge: true, inTabBar: true },
        { name: 'Policies', href: '/parent/family/policies', icon: DocumentTextIcon, testId: 'nav-parent-policies' },
        { name: 'Forms', href: `${base}/forms`, icon: DocumentTextIcon, testId: 'nav-forms', showBadge: true },
        { name: 'Authorised pickup', href: '/parent/account/pickup-contacts', icon: ClipboardDocumentCheckIcon, testId: 'nav-parent-pickup-contacts' },
        { name: 'Child details', href: `${base}/details`, icon: IdentificationIcon, testId: 'nav-child-details' },
        { name: 'Notifications', href: '/settings/notifications', icon: BellIcon, testId: 'nav-notifications' },
        { name: 'Account', href: '/settings/profile', icon: UserCircleIcon, testId: 'nav-account' },
      ];
    }

    // Staff / Location Admin / Org Admin inside a location
    const isAdmin = ['LOCATION_ADMIN', 'ORG_ADMIN', 'SUPER_ADMIN'].includes(
      activeLocationRole || activeRole
    );
    // Same local derivation as isAdmin above (activeLocationRole falls back
    // to activeRole) — the outer isOrgLevel from useAuth() is based on
    // activeRole alone and would miss a SUPER_ADMIN scoped into a location
    // via activeLocationRole, same reasoning isAdmin already follows here.
    const isOrgLevelHere = ['ORG_ADMIN', 'SUPER_ADMIN'].includes(activeLocationRole || activeRole);
    // Staff-or-above at this location (same local-derivation pattern as isAdmin).
    // Used for surfaces STAFF may see read-only, e.g. the learning-goals catalogue.
    const isStaffOrAboveHere = ['STAFF', 'LOCATION_ADMIN', 'ORG_ADMIN', 'SUPER_ADMIN'].includes(
      activeLocationRole || activeRole
    );
    return [
      { name: 'Dashboard', href: scopedPath('/dashboard'), icon: HomeIcon },
      { name: 'Students', href: scopedPath('/students'), icon: AcademicCapIcon },
      { name: 'Classes', href: scopedPath('/classes'), icon: RectangleGroupIcon },
      {
        name: 'Calendar',
        href: scopedPath('/calendar'),
        icon: CalendarIcon,
        testId: 'nav-calendar',
      },
      { name: 'Forms', href: scopedPath('/forms'), icon: DocumentTextIcon, showBadge: true },
      {
        name: 'Messages',
        href: scopedPath('/messages'),
        icon: ChatBubbleLeftIcon,
        showMessagesBadge: true,
      },
      ...(isAdmin ? [{ name: 'Team', href: scopedPath('/team'), icon: UserGroupIcon }] : []),
      // ── COMPLIANCE bucket ──────────────────────────────────────────────
      // A collapsible group (see sectionHeader render logic). Every
      // compliance-adjacent surface lives here so the top-level nav stays
      // short. Items with the SAME sectionHeader must stay CONTIGUOUS — the
      // header renders above the first item of a run and the group ends when
      // the sectionHeader value changes, so an item without it (or a different
      // header) in the middle would split the group. Collapsed by default.
      // Hidden for parents (no compliance concept for them).
      {
        name: 'Fire Drills',
        href: scopedPath('/fire-drills'),
        icon: FireIcon,
        sectionHeader: 'Compliance',
        testId: 'nav-fire-drills',
      },
      // A non-admin STAFF member has no other way to reach their own
      // compliance profile: the "Staff Compliance" register nav item below
      // is admin-only, and Team.tsx's member list is backed by an
      // admin-only endpoint, so it renders empty for STAFF. This is their
      // only nav path to self-service add-credential.
      ...(!isAdmin && user
        ? [
            {
              name: 'My Compliance',
              href: scopedPath(`/staff/${user.id}/compliance`),
              icon: DocumentTextIcon,
              sectionHeader: 'Compliance',
              testId: 'nav-my-compliance',
            },
          ]
        : []),
      {
        name: 'Incident Register',
        href: scopedPath('/incidents'),
        icon: ExclamationTriangleIcon,
        sectionHeader: 'Compliance',
        testId: 'nav-incident-register',
      },
      {
        name: 'Cleaning',
        href: scopedPath('/cleaning'),
        icon: BeakerIcon,
        sectionHeader: 'Compliance',
        testId: 'nav-cleaning',
      },
      // One "Checklists" entry only: sign-off and item-management are admin-only
      // TABS inside the Checklists page (Today / Sign-off / Manage items), not
      // extra top-level nav entries crowding the sidebar.
      {
        name: 'Checklists',
        href: scopedPath('/checklists'),
        icon: ClipboardDocumentListIcon,
        sectionHeader: 'Compliance',
        testId: 'nav-checklists',
      },
      {
        name: 'Policies',
        href: scopedPath('/policies'),
        icon: DocumentTextIcon,
        sectionHeader: 'Compliance',
        testId: 'nav-policies',
      },
      // Attendance register (Tusla) — admin-only. The page itself also gates
      // on isAdmin (see AttendancePage.tsx), so a direct URL hit is covered
      // even without the nav link.
      ...(isAdmin
        ? [
            {
              name: 'Attendance',
              href: scopedPath('/attendance'),
              icon: ClipboardDocumentCheckIcon,
              sectionHeader: 'Compliance',
              testId: 'nav-attendance',
            },
          ]
        : []),
      // Staff compliance register (Garda vetting / credentials) — admin-only.
      // The page itself also gates on isAdmin (see StaffComplianceRegister.tsx),
      // so a direct URL hit is covered even without the nav link.
      ...(isAdmin
        ? [
            {
              name: 'Staff Compliance',
              href: scopedPath('/staff-compliance'),
              icon: DocumentTextIcon,
              sectionHeader: 'Compliance',
              testId: 'nav-staff-compliance',
            },
          ]
        : []),
      // Retention plan admin (Task 21 — clocks only). LOCATION_ADMIN+ only; the
      // page itself also gates on isAdmin so a direct URL hit is covered.
      ...(isAdmin
        ? [
            {
              name: 'Retention',
              href: scopedPath('/retention'),
              icon: ArchiveBoxIcon,
              sectionHeader: 'Compliance',
              testId: 'nav-retention',
            },
          ]
        : []),
      // Per-org custom incident fields admin (Task 24) — ORG_ADMIN/SUPER_ADMIN
      // only, matching the backend's org:manage gate on the PUT endpoint (a
      // single location's admin must not configure org-wide fields).
      ...(isOrgLevelHere
        ? [
            {
              name: 'Incident Fields',
              href: scopedPath('/incident-fields'),
              icon: ExclamationTriangleIcon,
              sectionHeader: 'Compliance',
              testId: 'nav-incident-fields',
            },
          ]
        : []),

      // ── CURRICULUM bucket ──────────────────────────────────────────────
      // Must come AFTER the whole Compliance run (a new sectionHeader value
      // starts a new group). Aistéar learning goals — admins manage, STAFF
      // view read-only. Collapsed by default.
      ...(isStaffOrAboveHere
        ? [
            {
              name: 'Learning Goals',
              href: scopedPath('/learning-goals'),
              icon: SparklesIcon,
              sectionHeader: 'Curriculum',
              testId: 'nav-learning-goals',
            },
          ]
        : []),
    ];
  }, [
    effectiveIsParent,
    scopedPath,
    activeLocationRole,
    activeRole,
    homeChildId,
    computedParentHomeLabel,
  ]);

  // Choose nav based on current scope level
  const navigation = (() => {
    if (isSuperAdmin && !isInOrg) return superAdminTopNav; // SUPER_ADMIN at top level
    if (isOrgLevel && !isInLocation) return orgLevelNav; // ORG_ADMIN or SUPER_ADMIN in org
    return locationLevelNav; // Inside a location
  })();

  // Invitation count for Forms badge
  const { data: invitationCountData } = useGetInvitationCountQuery(undefined, {
    skip: !isParent,
    pollingInterval: 300_000,
    skipPollingIfUnfocused: true,
    refetchOnFocus: true,
  });
  const invitationCount = invitationCountData?.count || 0;

  // Unread message count for Messages badge — only shown to recipients
  // (active role PARENT). Admins switch context to parent if they want to
  // see their own inbox; we deliberately do not surface parent unread
  // counts inside an admin's sidebar.
  const { data: unreadCountData } = useGetMyUnreadCountQuery(undefined, {
    skip: !user || !isParent,
    pollingInterval: 300_000,
    skipPollingIfUnfocused: true,
    refetchOnFocus: true,
  });
  const unreadMessageCount = unreadCountData?.count || 0;

  // Updates is a child page, so its badge counts that child's unread
  // observations only — not the family-wide total. Counted server-side so it
  // stays exact past the 50-delivery page the switcher's breakdown reads.
  const { data: updatesUnreadData } = useGetMyObservationsUnreadCountQuery(
    { studentId: homeChildId ?? undefined },
    {
      skip: !homeChildId || !isParent,
      pollingInterval: 300_000,
      skipPollingIfUnfocused: true,
      refetchOnFocus: true,
    }
  );
  const updatesBadge = homeChildId ? updatesUnreadData?.count ?? 0 : 0;

  // Releasing the device's push subscription is part of signing out.
  //
  // A push subscription belongs to the BROWSER, but is stored against the user
  // who created it. Nothing released it on logout, so a device that had been
  // signed in as a parent stayed registered as that parent forever — and kept
  // receiving their pushes even after someone else signed in on it. In testing
  // that surfaced as a staff member replying to a parent and then getting a
  // push for their own reply: the notification was correctly addressed to the
  // parent, whose subscription was still this very device.
  //
  // Best-effort and awaited-but-never-blocking: a failure here must not trap
  // someone in a session they asked to leave.
  const { unsubscribe: unsubscribePushDevice } = usePushNotifications();

  const handleLogout = useCallback(() => {
    void unsubscribePushDevice()
      .catch(() => undefined)
      .finally(() => {
        dispatch(logout());
        dispatch(localApi.util.resetApiState());
        navigate('/login');
      });
  }, [dispatch, navigate, unsubscribePushDevice]);

  const handleBackToOrg = useCallback(() => {
    exitLocation();
  }, [exitLocation]);

  /** Short location name: "Main Campus" from "Green Valley Academy - Main Campus" */
  const shortName = (name?: string) => {
    if (!name) return '';
    const parts = name.split(' - ');
    return parts.length > 1 ? parts[parts.length - 1] : name;
  };

  // ========== Top bar content ==========
  const renderTopBar = () => {
    return (
      <div
        className="pwa-titlebar sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm"
        data-testid="app-topbar"
      >
        <div className="flex items-center justify-between px-4 py-2">
          {/* Left: mobile menu + breadcrumb */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label="Open navigation menu"
              data-testid="mobile-menu-button"
              className="lg:hidden -ml-0.5 h-10 w-10 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900"
              onClick={() => setSidebarOpen(true)}
            >
              <Bars3Icon className="h-6 w-6" />
            </button>

            {/* Back navigation for org/super admin */}
            {isSuperAdmin && isInOrg && !isInLocation && (
              <button
                data-testid="back-to-top"
                onClick={() => exitOrg()}
                className="hidden lg:inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Back to Organizations
              </button>
            )}
            {isOrgLevel && isInLocation && (
              <button
                data-testid="back-to-org"
                onClick={handleBackToOrg}
                className="hidden lg:inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Back to Organization
              </button>
            )}
          </div>

          {/* Right: location indicator + role badge + user */}
          <div className="flex items-center gap-3">
            {/* Child switcher — the only control that selects the active child.
                Sits beside the role picker for hybrid (parent + staff) users so
                they can switch axes independently: role first, then child.
                Family pages name no child, so they show the page title; the
                chooser IS the child picker, so the slot stays empty there. */}
            {effectiveIsParent &&
              (isFamilyPage ? (
                <span data-testid="parent-page-title" className="text-sm font-semibold text-gray-900">
                  {routerLocation.pathname.includes('/policies') ? 'Policies' : 'Inbox'}
                </span>
              ) : isChooserPage ? null : (
                <ChildSwitcher />
              ))}

            {/* Role/membership picker — shown when the user has more than one
                membership to flip between (including the synthetic Platform
                entry we inject for hybrid super admins). */}
            {switchableMemberships.length > 1 && (
              <div ref={locationPickerRef} className="relative">
                <button
                  data-testid="location-picker"
                  aria-label="Switch location"
                  onClick={() => setLocationPickerOpen(!locationPickerOpen)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setLocationPickerOpen(false);
                  }}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200 hover:bg-gray-100 transition-colors"
                >
                  <BuildingOfficeIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  {/* Location name is redundant alongside the kid picker (which
                      already carries the location subtitle), so hide it on the
                      smaller viewports for parent-active sessions. The role chip
                      to the right still gives them a way to switch back. */}
                  <span
                    className={`text-sm font-medium text-gray-700 max-w-[160px] truncate ${
                      effectiveIsParent ? 'hidden md:inline' : ''
                    }`}
                  >
                    {shortName(activeLocationName) ||
                      activeOrgName ||
                      activeOrganizationName ||
                      'Select role'}
                  </span>
                  {(() => {
                    // Always read from the active membership (activeRole from
                    // useAuth). effectiveIsParent folds in activeLocationRole,
                    // which is hard-coded to the first location membership and
                    // pins the badge to "Parent" for hybrid Parent+Staff users
                    // even after they toggle to Staff (Bug 5).
                    const displayRole = activeRole || activeLocationRole;
                    if (!displayRole) return null;
                    return (
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded-full ${
                          ROLE_COLORS[displayRole] || 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {ROLE_LABELS[displayRole] || displayRole}
                      </span>
                    );
                  })()}
                  <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400" />
                </button>
                {locationPickerOpen && (
                  <div className="absolute right-0 mt-1 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 max-h-80 overflow-y-auto">
                    {switchableMemberships.map((m, idx) => {
                      const realIndex =
                        user?.memberships?.findIndex(
                          (um) =>
                            um.locationId === m.locationId &&
                            um.organizationId === m.organizationId &&
                            um.role === m.role
                        ) ?? idx;
                      const activeMem = user?.memberships?.[activeMembershipIndex];
                      const isActive =
                        activeMem?.locationId === m.locationId &&
                        activeMem?.organizationId === m.organizationId &&
                        activeMem?.role === m.role;
                      const roleLabel = ROLE_LABELS[String(m.role)] || String(m.role);
                      return (
                        <button
                          key={`${m.locationId || m.organizationId}-${m.role}-${idx}`}
                          onClick={async () => {
                            const prevIndex = activeMembershipIndex;
                            const newIndex = realIndex >= 0 ? realIndex : idx;
                            dispatch(switchMembership(newIndex));
                            setLocationPickerOpen(false);

                            // Sync with backend session
                            try {
                              await switchContext({
                                role: String(m.role),
                                organizationId: m.organizationId || undefined,
                                locationId: m.locationId || undefined,
                              }).unwrap();
                            } catch {
                              dispatch(switchMembership(prevIndex));
                            }

                            // Navigate based on context type. For a PARENT
                            // membership, land on the child that lives at
                            // the new location (or the chooser when the
                            // parent has 2+ kids there) so the header, URL
                            // and visible child agree — otherwise the user
                            // sees a stale kid from the old location and
                            // scoped queries (forms, classes) 403 (Bug 9).
                            if (String(m.role) === 'PARENT' && m.locationId) {
                              const here = parentChildren.filter(
                                (c) => c.locationId === m.locationId
                              );
                              if (here.length === 1) {
                                navigate(`/parent/${here[0].id}`);
                              } else {
                                navigate('/parent/choose');
                              }
                            } else {
                              navigate('/dashboard');
                            }
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 ${
                            isActive ? 'bg-primary-50' : ''
                          }`}
                        >
                          {isActive ? (
                            <CheckIcon className="h-4 w-4 text-primary-600 flex-shrink-0" />
                          ) : (
                            <span className="w-4 flex-shrink-0" />
                          )}
                          <div className="flex flex-col items-start min-w-0 flex-1">
                            <span className="truncate text-gray-700 text-sm">
                              {m.locationName || m.organizationName || 'Platform'}
                            </span>
                            {m.locationName && m.organizationName && (
                              <span className="truncate text-gray-400 text-xs">
                                {m.organizationName}
                              </span>
                            )}
                          </div>
                          <span
                            className={`ml-auto text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                              ROLE_COLORS[String(m.role)] || 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {roleLabel}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Static role indicator — only shown when the dropdown is NOT
                visible (single membership) and the parent kid-picker isn't
                already standing in for it. */}
            {!(switchableMemberships.length > 1) && !effectiveIsParent && (
              <div
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200"
                data-testid="location-indicator"
              >
                <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-700 max-w-[160px] truncate">
                  {shortName(activeLocationName) ||
                    activeOrgName ||
                    activeOrganizationName ||
                    'Platform'}
                </span>
                <span
                  className={`text-xs px-1.5 py-0.5 rounded-full ${
                    ROLE_COLORS[activeRole] || 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {ROLE_LABELS[activeRole] || activeRole}
                </span>
              </div>
            )}

            {/* User menu dropdown */}
            <div className="relative" ref={userMenuRef}>
              <button
                data-testid="user-menu"
                aria-label="User menu"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setUserMenuOpen(false);
                }}
                className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900"
              >
                <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-xs font-medium text-primary-600">
                    {user?.firstName?.[0]}
                    {user?.lastName?.[0]}
                  </span>
                </div>
                <span className="hidden sm:block">
                  {user?.firstName} {user?.lastName}
                </span>
                <ChevronDownIcon className="h-3.5 w-3.5" />
              </button>
              {userMenuOpen && (
                <div
                  data-testid="user-menu-dropdown"
                  className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
                >
                  <div className="px-3 py-2 border-b">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                  </div>
                  <Link
                    to="/settings/profile"
                    onClick={() => setUserMenuOpen(false)}
                    className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Personal Information
                  </Link>
                  <Link
                    to="/settings/memberships"
                    onClick={() => setUserMenuOpen(false)}
                    className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Memberships
                  </Link>
                  <Link
                    to="/settings/security"
                    onClick={() => setUserMenuOpen(false)}
                    className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Security
                  </Link>
                  {/* /settings/notifications existed and worked, but nothing
                      linked to it from anywhere — this menu is how people reach
                      their account settings, so the page (push toggle, per-feature
                      preferences, quiet hours) was unreachable without typing the
                      URL. */}
                  <Link
                    to="/settings/notifications"
                    data-testid="user-menu-notifications"
                    onClick={() => setUserMenuOpen(false)}
                    className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Notifications
                  </Link>
                  <div className="border-t">
                    <button
                      data-testid="sign-out"
                      onClick={handleLogout}
                      className="block w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ========== Sidebar nav rendering ==========
  const renderNavItems = (items: NavItem[], isMobile = false) => {
    // Exactly one active item — see activeNavIndex for the matching rule (path
    // boundary, not bare startsWith; most-specific-then-first on ties). This
    // replaced a startsWith check that lit up Home + Forms + the current page
    // all at once on the parent nav.
    const bestActiveIdx = activeNavIndex(routerLocation.pathname, items);

    return (
      <>
        {/* Back to Organizations button for SUPER_ADMIN inside an org */}
        {isSuperAdmin && isInOrg && !isInLocation && (
          <div className="px-2 mb-3">
            <button
              onClick={() => exitOrg()}
              className="w-full flex items-center gap-2 px-2 py-2 text-xs font-medium text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-50"
            >
              <ArrowLeftIcon className="h-3.5 w-3.5" />
              Back to Organizations
            </button>
          </div>
        )}
        {/* Back to Org button for org-level users inside a location */}
        {isOrgLevel && isInLocation && (
          <div className="px-2 mb-3">
            <button
              onClick={handleBackToOrg}
              className="w-full flex items-center gap-2 px-2 py-2 text-xs font-medium text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-50"
            >
              <ArrowLeftIcon className="h-3.5 w-3.5" />
              Back to Org
            </button>
          </div>
        )}

        {items.map((item, idx) => {
          // The phone drawer lists only what the parent tab bar does not. When
          // the tab bar is absent (no child decided yet, or the chooser) the
          // drawer must keep these items or a cold family page has no way to
          // any child.
          if (isMobile && effectiveIsParent && item.inTabBar && parentTabBarVisible) return null;
          const isActive = idx === bestActiveIdx;
          const showBadge = item.showBadge && invitationCount > 0;
          const showUnreadBadge = item.showMessagesBadge && unreadMessageCount > 0;
          const showObservationsUnreadBadge =
            item.showObservationsBadge && updatesBadge > 0;
          // Section header logic. Render an interactive disclosure button
          // above the first item of each named group (or whenever the
          // sectionHeader value changes). Headers persist their
          // expanded/collapsed state to localStorage per section name.
          // Mobile always renders expanded — the mobile sidebar is a
          // transient flyout where hiding items behind an extra tap costs
          // more than the vertical space win.
          const prevSection = idx > 0 ? items[idx - 1].sectionHeader : undefined;
          const showSectionHeader = item.sectionHeader && item.sectionHeader !== prevSection;
          const isInSection = !!item.sectionHeader;
          // Collapsed BY DEFAULT: a section is expanded only when the user has
          // explicitly expanded it (stored `false`). Absent preference OR stored
          // `true` → collapsed. This keeps the sidebar short on first load (the
          // whole point of the buckets) while still honouring a user's choice to
          // pin a section open. Mobile always renders expanded.
          const sectionCollapsed =
            !isMobile && isInSection && collapsedSections[item.sectionHeader!] !== false;
          // Hide items in collapsed sections (but still render the header
          // so the user can re-expand).
          if (sectionCollapsed && !showSectionHeader) return null;
          return (
            <React.Fragment key={item.name}>
              {showSectionHeader && !isMobile && (
                <button
                  type="button"
                  onClick={() => toggleSection(item.sectionHeader!)}
                  aria-expanded={!sectionCollapsed}
                  aria-controls={`nav-section-items-${item
                    .sectionHeader!.toLowerCase()
                    .replace(/\s+/g, '-')}`}
                  className="w-full flex items-center justify-between px-2 pt-4 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-600"
                  data-testid={`nav-section-${item
                    .sectionHeader!.toLowerCase()
                    .replace(/\s+/g, '-')}`}
                >
                  <span>{item.sectionHeader}</span>
                  <ChevronDownIcon
                    className={`h-3.5 w-3.5 transition-transform ${
                      sectionCollapsed ? '-rotate-90' : ''
                    }`}
                  />
                </button>
              )}
              {showSectionHeader && isMobile && (
                // Mobile: static label, no toggle. Same testid so tests
                // still find it; aria-expanded omitted since there's no
                // interaction.
                <div
                  className="px-2 pt-4 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-400"
                  data-testid={`nav-section-${item
                    .sectionHeader!.toLowerCase()
                    .replace(/\s+/g, '-')}`}
                >
                  {item.sectionHeader}
                </div>
              )}
              {sectionCollapsed ? null : (
                <Link
                  to={item.href}
                  data-testid={item.testId || `nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`${
                    isActive
                      ? 'bg-primary-100 text-primary-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } group flex items-center px-2 py-2 ${
                    isMobile ? 'text-base' : 'text-sm'
                  } font-medium rounded-md`}
                  onClick={() => isMobile && setSidebarOpen(false)}
                >
                  <item.icon
                    className={`${
                      isActive ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                    } ${isMobile ? 'mr-4' : 'mr-3'} flex-shrink-0 h-6 w-6`}
                  />
                  <span className="flex-1">{item.name}</span>
                  {showBadge && (
                    <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      {invitationCount}
                    </span>
                  )}
                  {showUnreadBadge && (
                    <span
                      // Scope the testid to the desktop sidebar only so Playwright's
                      // strict mode doesn't trip over the mobile-sidebar duplicate
                      // (both branches render the same nav array via renderNavItems).
                      data-testid={isMobile ? undefined : 'messages-unread-badge'}
                      className="ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"
                      aria-label={`${unreadMessageCount} unread message${
                        unreadMessageCount === 1 ? '' : 's'
                      }`}
                    >
                      {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                    </span>
                  )}
                  {showObservationsUnreadBadge && (
                    <span
                      data-testid={isMobile ? undefined : 'observations-unread-badge'}
                      className="ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"
                      aria-label={`${updatesBadge} unread observation${
                        updatesBadge === 1 ? '' : 's'
                      }`}
                    >
                      {updatesBadge > 99 ? '99+' : updatesBadge}
                    </span>
                  )}
                </Link>
              )}
            </React.Fragment>
          );
        })}
      </>
    );
  };

  const renderSidebarFooter = () => {
    const locationName = isInLocation ? shortName(activeLocationName) : null;
    const orgName = isInOrg ? activeOrgName || activeOrganizationName : null;

    if (!locationName && !orgName) {
      return (
        <div className="flex-shrink-0 border-t border-gray-200 px-4 py-3">
          <p className="text-xs text-gray-400">{brand.name} Platform</p>
        </div>
      );
    }

    return (
      <div className="flex-shrink-0 border-t border-gray-200 px-4 py-3">
        {locationName && (
          <div className="flex items-center gap-2">
            <BuildingOfficeIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-700 truncate">{locationName}</p>
              {orgName && <p className="text-xs text-gray-400 truncate">{orgName}</p>}
            </div>
          </div>
        )}
        {!locationName && orgName && (
          <div className="flex items-center gap-2">
            <BuildingLibraryIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <p className="text-sm font-medium text-gray-700 truncate">{orgName}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div
        className={`fixed inset-0 z-40 lg:hidden ${sidebarOpen ? '' : 'hidden'}`}
        data-testid="app-sidebar-mobile"
      >
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="relative flex flex-col w-full max-w-xs bg-white h-full">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              aria-label="Close navigation menu"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex-shrink-0 flex items-center px-4">
              <h1
                className="text-xl font-bold text-gray-900 truncate"
                title={activeOrgName || activeOrganizationName || brand.name}
                data-testid="sidebar-brand"
              >
                {activeOrgName || activeOrganizationName || brand.name}
              </h1>
            </div>
            <nav className="mt-5 px-2 space-y-1">{renderNavItems(navigation, true)}</nav>
          </div>
          {renderSidebarFooter()}
        </div>
      </div>

      {/* Desktop sidebar */}
      <div
        className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0"
        data-testid="app-sidebar"
      >
        <div className="flex-1 flex flex-col min-h-0 border-r border-gray-200 bg-white">
          <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
            <div className="flex items-center flex-shrink-0 px-4">
              <h1
                className="text-xl font-bold text-gray-900 truncate"
                title={activeOrgName || activeOrganizationName || brand.name}
                data-testid="sidebar-brand"
              >
                {activeOrgName || activeOrganizationName || brand.name}
              </h1>
            </div>
            <nav className="mt-5 flex-1 px-2 bg-white space-y-1">{renderNavItems(navigation)}</nav>
          </div>
          {renderSidebarFooter()}
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col flex-1">
        {renderTopBar()}
        <NotificationBanner />
        <main className={`flex-1 ${effectiveIsParent ? 'pb-20 lg:pb-0' : ''}`}>
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
        {effectiveIsParent && parentTabBarVisible && (
          <ParentTabBar
            homeChildId={homeChildId}
            unreadMessages={unreadMessageCount}
            unreadObservations={updatesBadge}
          />
        )}
        {effectiveIsParent && <OtherChildrenNotice />}
      </div>
    </div>
  );
};

export default Layout;
